import { useCallback, useEffect, useMemo, useState } from "react";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import RefreshButton from "../../core/RefreshButton";

import { calibrationService } from "../../../services/CalibrationService";

export function listInitializer(
    apiFunction: (...args: any[]) => Promise<any>,
    formatFunction: (response: any) => any,
    setDataFunction: (data: any) => any,
    setErrorWithTimeout: (error: string, timeout: number) => any,
    listName: string,
    setIsLoading?: (isLoading: boolean) => any
) {
    return async (...args: any[]) => {
        try {
            if (setIsLoading) {
                setIsLoading(true);
            }
            let response = await apiFunction(...args);
            if (formatFunction) {
                response = formatFunction(response);
            }
            setDataFunction(response);
        } catch (error) {
            console.error(`There was an error initializing the ${listName}, ${error}`);
            setErrorWithTimeout(`Failed to initialize the ${listName}, ${error}`, 10000);
        } finally {
            if (setIsLoading) {
                setIsLoading(false);
            }
        }
    };
}

export type DropdownOption = {
    label?: string;
    value: string;
};

export const DropdownWithAutocomplete = ({
    label,
    value,
    setValue,
    options,
    refreshOptions,
    disabled,
    refreshDisabled,
    sortFunction,
    isLoading,
    styleClass,
    testid,
    freeSolo,
}: {
    label: string;
    value: string;
    setValue: (value: string) => any;
    options: { label?: string; value: string }[];
    refreshOptions: () => any;
    disabled: boolean;
    refreshDisabled: boolean;
    sortFunction?: (a: { label?: string; value: string }, b: { label?: string; value: string }) => number;
    isLoading: boolean;
    styleClass?: any;
    testid?: string;
    freeSolo?: boolean;
}) => {
    const displayOptions = sortFunction ? [...options].sort(sortFunction) : [...options];

    const handleSetValue = (newValue: DropdownOption | string | null) => {
        if (typeof newValue === "string") {
            // Directly use the string value for freeSolo
            setValue(newValue);
        } else if (newValue && "value" in newValue) {
            setValue(newValue.value);
        } else {
            setValue("");
        }
    };

    const getOptionLabel = (option: DropdownOption | string): string => {
        return typeof option === "string" ? option : option.label || option.value;
    };

    return (
        <FormControl className={styleClass} sx={{ width: "100%" }}>
            <Autocomplete
                id="autocomplete-box"
                options={displayOptions}
                getOptionLabel={getOptionLabel}
                freeSolo={freeSolo}
                value={options.find((option) => option.value === value) || (freeSolo && value ? value : null)}
                onChange={(event, newValue) => {
                    handleSetValue(newValue);
                }}
                onBlur={(event) => {
                    if (freeSolo) {
                        const inputValue = (event.target as HTMLInputElement).value;
                        if (inputValue !== value) {
                            setValue(inputValue);
                        }
                    }
                }}
                disabled={disabled || isLoading}
                renderInput={(params) => (
                    <TextField {...params} label={label} variant="outlined" style={{ width: "100%" }} />
                )}
                data-testid={testid}
            />
            {!refreshDisabled && (
                <RefreshButton
                    onClickFunction={refreshOptions}
                    setValue={() => setValue("")}
                    valueName={label.toLowerCase()}
                    disabledBoolean={disabled}
                    ariaLabel={`refresh ${label.toLowerCase()}`}
                    isLoading={isLoading}
                />
            )}
        </FormControl>
    );
};

type ReusableDropdownProps = {
    retrieveOptions: (...args: any[]) => Promise<string[]>;
    retrieveOptionsArgs: any[];
    value: string;
    setValue: (value: string) => any;
    setError?: (error: string) => any;
    dropdownName: string;
    listName: string;
    sortFunction?: (a: DropdownOption, b: DropdownOption) => number;
    disabledCondition?: boolean;
    styleClass?: any;
    testid?: string;
    freeSolo?: boolean;
};

function formatOptions(options: string[]) {
    return options?.map((option: string) => ({ label: option, value: option }));
}

function ReusableDropdown({
    retrieveOptions,
    retrieveOptionsArgs,
    value,
    setValue,
    setError,
    dropdownName,
    listName,
    sortFunction,
    disabledCondition,
    styleClass,
    testid,
    freeSolo,
}: ReusableDropdownProps) {
    const [optionsLoading, setOptionsLoading] = useState(false);
    const [options, setOptions] = useState([]);

    const retrieveWithArguments = useCallback(
        () => retrieveOptions(...retrieveOptionsArgs),
        [retrieveOptions, retrieveOptionsArgs]
    );

    const initializeOptions = useMemo(
        () =>
            listInitializer(
                retrieveWithArguments,
                formatOptions,
                setOptions,
                (error: string) => setError && setError(error),
                listName,
                setOptionsLoading
            ),
        [retrieveWithArguments, setOptions, setError, listName, setOptionsLoading]
    );

    useEffect(() => {
        // Clear our value when our dependency parameters change in some way
        setValue("");
        initializeOptions();
    }, [retrieveOptionsArgs, initializeOptions, setValue]);

    if (!sortFunction) {
        sortFunction = (a, b) => {
            return a.value.localeCompare(b.value);
        };
    }

    return (
        <DropdownWithAutocomplete
            label={dropdownName}
            value={value}
            setValue={setValue}
            options={options}
            disabled={!!disabledCondition}
            refreshOptions={initializeOptions}
            refreshDisabled={false}
            sortFunction={sortFunction}
            isLoading={optionsLoading}
            styleClass={styleClass}
            testid={testid}
            freeSolo={freeSolo}
        />
    );
}
export const ClientsDropdown = ({
    value,
    setValue,
    setError,
    styleClass,
    getClients,
    freeSolo,
}: {
    value: string;
    setValue: (value: string) => any;
    setError?: (error: string) => any;
    styleClass?: any;
    getClients?: () => Promise<string[]>;
    freeSolo?: boolean;
}) => {
    const retrieveClients = useCallback(async () => {
        if (getClients) {
            return getClients();
        }
        const response = await calibrationService.getClients();
        return response.data;
    }, [getClients]);
    const retrieveOptionsArgs = useMemo(() => [], []);
    return ReusableDropdown({
        retrieveOptions: retrieveClients,
        retrieveOptionsArgs: retrieveOptionsArgs,
        value: value,
        setValue: setValue,
        setError: setError,
        dropdownName: "Client",
        listName: "clients",
        disabledCondition: false,
        styleClass: styleClass,
        testid: "client-dropdown",
        freeSolo: freeSolo,
    });
};

export const ProjectsDropdown = ({
    clientName,
    value,
    setValue,
    setError,
    styleClass,
    freeSolo,
}: {
    clientName: string;
    value: string;
    setValue: (value: string) => any;
    setError?: (error: string) => any;
    styleClass?: any;
    freeSolo?: boolean;
}) => {
    const retrieveProjects = useCallback(async (client: string) => {
        if (!client) {
            return [];
        }
        const response = await calibrationService.getProjectNames(client);
        return response.data;
    }, []);

    const retrieveOptionArgs = useMemo(() => [clientName], [clientName]);

    return ReusableDropdown({
        retrieveOptions: retrieveProjects,
        retrieveOptionsArgs: retrieveOptionArgs,
        value: value,
        setValue: setValue,
        setError: setError,
        dropdownName: "Project",
        listName: "projects",
        disabledCondition: !clientName,
        styleClass: styleClass,
        freeSolo: freeSolo,
    });
};

export const MultiProjectsDropdown = ({
    clientName,
    value,
    setValue,
    setError,
    styleClass,
}: {
    clientName: string;
    value: string[];
    setValue: (value: string[]) => any;
    setError?: (error: string) => any;
    styleClass?: any;
}) => {
    const [optionsLoading, setOptionsLoading] = useState(false);
    const [options, setOptions] = useState<string[]>([]);

    const fetchProjects = useCallback(async () => {
        if (!clientName) {
            setOptions([]);
            return;
        }
        try {
            setOptionsLoading(true);
            const response = await calibrationService.getProjectNames(clientName);
            setOptions(response.data || []);
        } catch (error: any) {
            console.error("Error fetching projects:", error);
            if (setError) {
                setError(`Failed to load projects: ${error?.message || String(error)}`);
            }
        } finally {
            setOptionsLoading(false);
        }
    }, [clientName, setError]);

    useEffect(() => {
        setValue([]);
        fetchProjects();
    }, [clientName]);

    const sortedOptions = useMemo(() => [...options].sort((a, b) => a.localeCompare(b)), [options]);
    const allSelected = sortedOptions.length > 0 && value.length === sortedOptions.length;

    return (
        <FormControl className={styleClass} sx={{ width: "100%" }}>
            <Autocomplete
                multiple
                id="multi-projects-autocomplete"
                options={sortedOptions}
                getOptionLabel={(option) => option}
                isOptionEqualToValue={(option, val) => option === val}
                value={value}
                onChange={(event, newValue) => {
                    setValue(newValue);
                }}
                disabled={!clientName || optionsLoading}
                renderInput={(params) => (
                    <TextField {...params} label="Projects" variant="outlined" style={{ width: "100%" }} />
                )}
                data-testid="multi-projects-dropdown"
            />
            <Button
                onClick={() => setValue(allSelected ? [] : sortedOptions)}
                disabled={!clientName || optionsLoading || sortedOptions.length === 0}
                size="small"
                sx={{ alignSelf: "flex-start", textTransform: "none" }}
                data-testid="multi-projects-select-all"
            >
                {allSelected ? `Clear all (${sortedOptions.length})` : `Select all (${sortedOptions.length})`}
            </Button>
            <RefreshButton
                onClickFunction={fetchProjects}
                setValue={() => setValue([])}
                valueName="projects"
                disabledBoolean={!clientName}
                ariaLabel="refresh projects"
                isLoading={optionsLoading}
            />
        </FormControl>
    );
};

export const DatasetTypesDropdown = ({
    clientName,
    projectName,
    value,
    setValue,
    setError,
    allowedDatasetTypes,
    styleClass,
}: {
    clientName: string;
    projectName: string;
    value: string;
    setValue: (value: string) => any;
    setError?: (error: string) => any;
    allowedDatasetTypes?: string[];
    styleClass?: any;
}) => {
    const retrieveDatasetTypes = useCallback(
        async (client: string, project: string) => {
            if (!client || !project) {
                return [];
            }
            const response = await calibrationService.getDatasetTypes(client, project);
            const datasetTypes = response.data;
            const visibleDatasetTypes = datasetTypes.filter((datasetType: string) => {
                if (!allowedDatasetTypes) {
                    return true;
                }
                return allowedDatasetTypes.includes(datasetType);
            });
            return visibleDatasetTypes;
        },
        [allowedDatasetTypes]
    );
    const retrieveOptionsArgs = useMemo(() => [clientName, projectName], [clientName, projectName]);
    return ReusableDropdown({
        retrieveOptions: retrieveDatasetTypes,
        retrieveOptionsArgs: retrieveOptionsArgs,
        value: value,
        setValue: setValue,
        setError: setError,
        dropdownName: "Dataset Type",
        listName: "datasetTypes",
        disabledCondition: !clientName || !projectName,
        styleClass: styleClass,
    });
};

export const GroupDropdown = ({
    clientName,
    projectName,
    datasetType,
    value,
    setValue,
    setError,
    styleClass,
}: {
    clientName: string;
    projectName: string;
    datasetType: string;
    value: string;
    setValue: (value: string) => any;
    setError?: (error: string) => any;
    styleClass?: any;
}) => {
    const retrieveGroups = useCallback(async (client: string, project: string, datasetType: string) => {
        if (!client || !project || !datasetType) {
            return [];
        }
        const response = await calibrationService.getGroups(client, project, datasetType);
        return response.data;
    }, []);

    const retrieveOptionArgs = useMemo(
        () => [clientName, projectName, datasetType],
        [clientName, projectName, datasetType]
    );

    return ReusableDropdown({
        retrieveOptions: retrieveGroups,
        retrieveOptionsArgs: retrieveOptionArgs,
        value: value,
        setValue: setValue,
        setError: setError,
        dropdownName: "Group",
        listName: "groups",
        disabledCondition: !clientName || !projectName || !datasetType,
        styleClass: styleClass,
    });
};

export const DatetimeDropdown = ({
    clientName,
    projectName,
    datasetType,
    group,
    value,
    setValue,
    setError,
    name,
    disabled,
    styleClass,
    getDatetimes,
}: {
    clientName: string;
    projectName: string;
    datasetType: string;
    group: string;
    value: string;
    setValue: (value: string) => any;
    setError?: (error: string) => any;
    name?: string;
    disabled?: boolean;
    styleClass?: any;
    getDatetimes?: () => Promise<string[]>;
}) => {
    const retrieveDatetimes = useCallback(
        async (client: string, project: string, datasetType: string, group: string) => {
            if (getDatetimes) {
                return getDatetimes();
            }
            console.log(client, project, datasetType, group);
            if (!client || !project || !datasetType || !group) {
                return [];
            }
            const response = await calibrationService.getFileNames(client, project, group, datasetType);
            return response.data;
        },
        [getDatetimes]
    );

    const fullDisabledCondition = useMemo(
        () => !clientName || !projectName || !datasetType || !group || disabled,
        [clientName, projectName, datasetType, group, disabled]
    );

    const retrieveOptionArgs = useMemo(
        () => [clientName, projectName, datasetType, group],
        [clientName, projectName, datasetType, group]
    );

    return ReusableDropdown({
        retrieveOptions: retrieveDatetimes,
        retrieveOptionsArgs: retrieveOptionArgs,
        value: value,
        setValue: setValue,
        setError: setError,
        dropdownName: name || "Datetime",
        listName: "datetimes",
        sortFunction: (a, b) => {
            // this call to compare is intentionally swapped so we get descending date order
            return b.value.localeCompare(a.value);
        },
        disabledCondition: fullDisabledCondition,
        styleClass: styleClass,
        testid: "datetime-dropdown",
    });
};

export type ProcessTypeOption = {
    label?: string;
    value: string;
    description?: string;
};

export const ProcessTypesDropdown = ({
    clientName,
    projectName,
    value,
    setValue,
    setError,
    disabled,
    styleClass,
    onOptionsLoaded,
}: {
    clientName: string;
    projectName: string;
    value: string[];
    setValue: (value: string[]) => any;
    setError?: (error: string) => any;
    disabled?: boolean;
    styleClass?: any;
    onOptionsLoaded?: (options: ProcessTypeOption[]) => void;
}) => {
    const [optionsLoading, setOptionsLoading] = useState(false);
    const [options, setOptions] = useState<ProcessTypeOption[]>([]);

    const fetchProcessTypes = useCallback(async () => {
        try {
            setOptionsLoading(true);
            const response = await calibrationService.getProcessTypes();
            const formatted = (response.data || []).map((pt: { value: string; description: string }) => ({
                label: pt.value,
                value: pt.value,
                description: pt.description,
            }));

            setOptions(formatted);
            if (onOptionsLoaded) {
                onOptionsLoaded(formatted);
            }
        } catch (error: any) {
            console.error("Error fetching process types:", error);
            if (setError) {
                const errorMessage = error?.message || String(error);
                setError(`Failed to load process types: ${errorMessage}`);
            }
        } finally {
            setOptionsLoading(false);
        }
    }, [clientName, projectName, onOptionsLoaded, setError]);

    useEffect(() => {
        setValue([]);
        setOptions([]);
        fetchProcessTypes();
    }, [clientName, projectName]);

    const selectedOptions = options.filter((opt) => value.includes(opt.value));

    return (
        <FormControl className={styleClass} sx={{ width: "100%" }}>
            <Autocomplete
                multiple
                id="process-type-autocomplete"
                options={options}
                getOptionLabel={(option) => option.label || option.value}
                isOptionEqualToValue={(option, val) => option.value === val.value}
                value={selectedOptions}
                onChange={(event, newValue) => {
                    setValue(newValue.map((opt) => opt.value));
                //    const unique = [...new Map(newValue.map((opt) => [opt.value, opt])).values()];
                //    setValue(unique.map((opt) => opt.value));
                }}
                disabled={!!disabled || optionsLoading}
                renderInput={(params) => (
                    <TextField {...params} label="Process Types" variant="outlined" style={{ width: "100%" }} />
                )}
                data-testid="process-type-dropdown"
            />
            <RefreshButton
                onClickFunction={fetchProcessTypes}
                setValue={() => setValue([])}
                valueName="process types"
                disabledBoolean={!!disabled}
                ariaLabel="refresh process types"
                isLoading={optionsLoading}
            />
        </FormControl>
    );
};
